import { DocumentView } from "@/components/documents/DocumentView";
import { useEffect, useRef, useState } from "react";
import { FileDropZone } from "@/components/common/FileDropZone";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";
import { LocalizedError, localizedMessageOf } from "@/lib/localized-error";
import { Button } from "@/components/design/Button";
import { Switch } from "@/components/design/Switch";
import { ExtractionDocumentPicker } from "./ExtractionDocumentPicker";
import { ExtractionDialogFrame } from "./ExtractionDialogFrame";
import { ExtractionResultView } from "./ExtractionResultView";
import {
  refreshProfileSkills,
  resultOrStandIn,
  unscoredPowerCount,
  type ExtractionJobRecord,
  type ProcessResult,
} from "./extraction-run-changes";
import {
  CreateDocumentDocument,
  ExtractionJobResultDocument,
  PowersDataDocument,
  StartDocumentsExtractionDocument,
} from "@/graphql/generated/operations";
import { awaitDocumentsExtraction } from "@/lib/await-extraction";
import { validationResultMessage } from "@/lib/graphql-error";
import { MAX_DOCUMENT_UPLOAD_BYTES, MAX_DOCUMENTS_PER_RUN } from "@/lib/upload-limits";
import { ANALYZABLE_ACCEPT, isAnalyzableDocument } from "@/lib/analyzable-documents";

// genesis startDocumentsExtraction runs LLM extraction (Document Extraction
// service) over documents and writes the results onto the person's profile.
// This dialog covers the two silently-persisting pipelines — Power- (extractPowers)
// and Hardskill-/Fachkompetenz- (extractSkills) analysis — parameterised by `flags`.
// CV/profile analysis is a review-first flow and lives in ProfileExtractionDialog
// (the extractProfileFromDocument mutation), NOT here — genesis does not process a
// profile extraction through this path.
//
// Transport (genesis #113): the mutation returns immediately with a QUEUED job;
// progress and the final result stream over the documentsExtractionEvents
// subscription. This replaces the old blocking processDocuments await, which
// the BFF severed at GENESIS_TIMEOUT_MS (~30s) well before a minutes-long power
// extraction finished. On a dropped socket the outcome is recovered by polling
// the job status, with the completion notification as the durable fallback; it
// reopens the same result through ExtractionResultDialog (spark#1216).

export interface ExtractionFlags {
  extractPowers?: boolean;
  extractSkills?: boolean;
}

export interface ExtractionDoc {
  id: string;
  name: string;
  fileName?: string;
  // Folder the document lives in (null = "Ohne Ordner").
  groupId?: string | null;
  // Drives the newest-first order of the list.
  createdAt?: string | null;
}

interface PendingFile {
  fileName: string;
  mimeType: string;
  content: string; // base64 (no data-URL prefix)
}

function readBase64(file: File): Promise<PendingFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result);
      const comma = s.indexOf(",");
      resolve({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        content: comma >= 0 ? s.slice(comma + 1) : s,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function ExtractionDialog({
  title,
  flags,
  docs,
  groups = [],
  docsLoading = false,
  docsLoadError = false,
  personId,
  onClose,
  onDone,
}: {
  title: string;
  flags: ExtractionFlags;
  docs: readonly ExtractionDoc[];
  groups?: readonly { id: string; name: string }[];
  // While true the stock is still arriving, so an empty list says "not yet".
  docsLoading?: boolean;
  docsLoadError?: boolean;
  // Needed to persist newly uploaded files as Documents before analysing them.
  personId: string | null;
  onClose: () => void;
  onDone?: () => void;
}) {
  const intl = useIntl();
  const apollo = useApolloClient();
  const [startDocumentsExtraction] = useMutation(StartDocumentsExtractionDocument);
  const [createDocument] = useMutation(CreateDocumentDocument);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  // True once the background job has been accepted (a job id came back). From
  // that point the extraction is detached server-side, so the user may close the
  // dialog and keep working — the completion notification is the durable signal.
  const [jobAccepted, setJobAccepted] = useState(false);
  // Aborts the background event watch when the dialog is closed mid-run, so we
  // don't leave a dangling subscription/poll after the user walks away.
  const abortRef = useRef<AbortController | null>(null);
  // Everything this run has claimed: documents ticked in the list, files already read into
  // `pending`, and files a handler is still reading. One counter for both halves, because both move
  // asynchronously — a drop decides against a render-time `selectedIds`, a tick against a
  // render-time `pending` — so each would overshoot while the other is still in flight. The ref is
  // only ever touched from event handlers, never from inside a state updater.
  const spentRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProcessResult | null>(null);
  // The run as genesis recorded it; null when it could not be read.
  const [job, setJob] = useState<ExtractionJobRecord | null>(null);
  // How many of the 30 powers still have no score at all. A state fact, not a
  // report on this run — @adr SPARK-0045-a-power-with-no-indication-is-counted-not-explained@1.
  const [unscoredPowers, setUnscoredPowers] = useState<number | null>(null);
  // Whether freshly uploaded files are persisted as Documents. Off → the file
  // is only sent for analysis (ephemeral, not stored).
  const [persistUploads, setPersistUploads] = useState(true);

  // Stop watching the job if the dialog is unmounted (user closed it mid-run).
  useEffect(() => () => abortRef.current?.abort(), []);

  // Names already present in Documents (case-insensitive) - used to warn about
  // duplicates the user may have overlooked, and to reuse the existing doc.
  const existingNames = new Set(docs.map((d) => d.name.toLowerCase()));

  const total = pending.length + selectedIds.size;
  // Slots left in this run, for the selection controls. Files go through `spentRef` instead: this
  // value is render-time, and a second drop starting mid-read would see it unchanged.
  const room = Math.max(0, MAX_DOCUMENTS_PER_RUN - total);

  const sayTooMany = () =>
    setError(
      intl.formatMessage(
        {
          id: "save.extraction.tooMany",
          defaultMessage: "You can analyse at most {max} documents at once.",
        },
        { max: MAX_DOCUMENTS_PER_RUN },
      ),
    );

  async function addFiles(files: FileList | null) {
    if (!files) return;
    setError(null);
    const accepted: PendingFile[] = [];
    let claimed = 0;
    try {
      for (const f of Array.from(files)) {
        if (spentRef.current >= MAX_DOCUMENTS_PER_RUN) {
          sayTooMany();
          break;
        }
        if (!isAnalyzableDocument(f.name, f.type)) {
          setError(
            intl.formatMessage(
              {
                id: "save.extraction.unsupportedType",
                defaultMessage:
                  "“{name}” can’t be analysed. Supported: PDF, Word, Excel and images.",
              },
              { name: f.name },
            ),
          );
          continue;
        }
        if (f.size > MAX_DOCUMENT_UPLOAD_BYTES) {
          setError(
            intl.formatMessage(
              {
                id: "save.extraction.fileTooLarge",
                defaultMessage: "“{name}” is too large (max. 10 MB per file).",
              },
              { name: f.name },
            ),
          );
          continue;
        }
        spentRef.current += 1;
        claimed += 1;
        accepted.push(await readBase64(f));
      }
    } finally {
      // A read that threw never becomes a pending file, so its slot goes back.
      spentRef.current -= claimed - accepted.length;
    }
    if (accepted.length) setPending((p) => [...p, ...accepted]);
  }

  const toggleDoc = (id: string) => {
    if (selectedIds.has(id)) {
      spentRef.current -= 1;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }
    if (spentRef.current >= MAX_DOCUMENTS_PER_RUN) {
      // Said out loud rather than swallowed: the checkbox can still look available while a drop is
      // mid-read, because `room` is render-derived and `pending` has not committed yet.
      sayTooMany();
      return;
    }
    spentRef.current += 1;
    setSelectedIds((prev) => new Set(prev).add(id));
  };

  const toggleMany = (ids: readonly string[], select: boolean) => {
    const next = new Set(selectedIds);
    for (const id of ids) {
      if (!select) {
        if (next.delete(id)) spentRef.current -= 1;
        continue;
      }
      if (next.has(id)) continue;
      // Fills the remaining slots rather than refusing the whole group: picking 3 of 5 is what the
      // user wanted from "select all" once the run is nearly full.
      if (spentRef.current >= MAX_DOCUMENTS_PER_RUN) {
        sayTooMany();
        break;
      }
      next.add(id);
      spentRef.current += 1;
    }
    setSelectedIds(next);
  };

  async function readJob(jobId: string): Promise<ExtractionJobRecord | null> {
    try {
      const r = await apollo.query({
        query: ExtractionJobResultDocument,
        variables: { jobId },
        fetchPolicy: "network-only",
      });
      return r.data?.extractionJob ?? null;
    } catch {
      // The outcome is already known from the event or the poll; a failed read costs the run's changes,
      // and on the poll path its document rows too.
      logApplicationFailure("save.extraction.job-read", { level: "warn" });
      return null;
    }
  }

  async function countUnscoredPowers(): Promise<number | null> {
    try {
      const r = await apollo.query({ query: PowersDataDocument, fetchPolicy: "network-only" });
      return unscoredPowerCount(r.data?.smartAnalysisResults ?? []);
    } catch {
      // the count is a nice-to-have; ignore lookup failures
      return null;
    }
  }

  async function run() {
    if (busy || total === 0) return;
    setBusy(true);
    setError(null);
    setJobAccepted(false);
    setJob(null);
    setUnscoredPowers(null);
    try {
      const documents: { fileName: string; documentId?: string; content?: string }[] = [];
      const uploadFailed = intl.formatMessage({
        id: "save.extraction.uploadFailedDuplicate",
        defaultMessage: "Upload failed. A document with this name probably already exists.",
      });
      const analysisFailed = intl.formatMessage({
        id: "save.extraction.analysisFailed",
        defaultMessage: "Analysis failed.",
      });
      for (const f of pending) {
        // Reuse rather than re-upload: a file whose name is already in Documents is analysed from
        // the stored document instead. This is a deliberate choice to avoid uploading the same bytes
        // twice, NOT a consequence of a uniqueness rule - genesis has no unique constraint on
        // document_name, so it is a heuristic: with several same-named documents this picks the
        // first match arbitrarily.
        const existing = docs.find((d) => d.name.toLowerCase() === f.fileName.toLowerCase());
        if (existing) {
          documents.push({ fileName: existing.name, documentId: existing.id });
          continue;
        }
        if (!persistUploads) {
          // Analyse-only: send the content inline, don't store a Document.
          // Inline (ephemeral) analyses yield scores but no stored evidence -
          // genesis has no document to attach the quotes to.
          documents.push({ fileName: f.fileName, content: f.content });
          continue;
        }
        // Persist as a new Document (normal upload → no "Lebenslauf" tag →
        // source "Dateiupload"), then analyse it by id so evidence rows can
        // attach to the document and re-runs are deduplicated.
        if (!personId)
          throw new LocalizedError(
            intl.formatMessage({
              id: "save.extraction.notSignedIn",
              defaultMessage: "Not signed in - upload not possible.",
            }),
          );
        const cr = await createDocument({
          variables: {
            input: {
              data: {
                name: f.fileName,
                person: { id: personId },
                tags: [],
                // fileName is a top-level Document field; CreateFileDataInput
                // (content) only accepts mimeType + content.
                fileName: f.fileName,
                content: { mimeType: f.mimeType, content: f.content },
              },
              dryRun: false,
            },
          },
        });
        const cout = cr.data?.createDocument;
        if (cout?.__typename !== "CreateDocumentResult") {
          throw new LocalizedError(validationResultMessage(cout, uploadFailed));
        }
        documents.push({
          fileName: cout.result.fileName ?? cout.result.name ?? f.fileName,
          documentId: cout.result.id,
        });
      }
      for (const d of docs.filter((d) => selectedIds.has(d.id))) {
        documents.push({ fileName: d.name, documentId: d.id });
      }
      // The same document can arrive twice (a dropped file that already
      // existed + its checkbox selection); send each one once.
      const seen = new Set<string>();
      const uniqueDocuments = documents.filter((d) => {
        const key = d.documentId ?? d.fileName.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      // Fire the job: returns immediately with a QUEUED handle. The blocking
      // wait now lives on the subscription, not the HTTP request.
      const started = await startDocumentsExtraction({
        variables: {
          input: {
            documents: uniqueDocuments,
            extractPowers: Boolean(flags.extractPowers),
            // Required by the schema but not implemented on this path; profile
            // extraction is the separate extractProfileFromDocument flow.
            extractProfile: false,
            extractSkills: Boolean(flags.extractSkills),
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      }).catch((e: unknown) => {
        throw new LocalizedError(
          intl.formatMessage(
            {
              id: "save.extraction.analysisFailedWithReason",
              defaultMessage: "Analysis failed ({reason}).",
            },
            { reason: userErrorMessage(intl, e) },
          ),
          { cause: e },
        );
      });
      const jobId = started.data?.startDocumentsExtraction?.id;
      if (!jobId) throw new LocalizedError(analysisFailed);

      // Job accepted and now detached server-side: from here the user is free to
      // close the dialog and keep working (Close is enabled below).
      setJobAccepted(true);

      // Watch the job's events for the live result (status poll is the
      // dropped-socket fallback inside awaitDocumentsExtraction). Aborted if the
      // dialog is closed mid-run.
      const controller = new AbortController();
      abortRef.current = controller;
      const outcome = await awaitDocumentsExtraction(apollo, jobId, { signal: controller.signal });

      // Dialog was closed while the job ran — nothing to render.
      if (outcome.kind === "cancelled") {
        return;
      }

      if (outcome.kind === "timeout") {
        // The job is still running server-side; its result arrives as a durable
        // notification that deep-links back here.
        throw new LocalizedError(
          intl.formatMessage({
            id: "save.extraction.stillRunning",
            defaultMessage:
              "The analysis is taking a while and keeps running in the background. You’ll get a notification when it’s done.",
          }),
        );
      }

      // The job is the same record the completion notification reopens, so the panel shows what the
      // run wrote rather than a before/after diff of the profile, and a payload the event lost
      // (the poll-recovered path) comes back from it. genesis stores the state before it emits the
      // terminal event, so the read cannot overtake it.
      const [recorded, unscored] = await Promise.all([
        readJob(jobId),
        flags.extractPowers ? countUnscoredPowers() : null,
      ]);
      const out = outcome.result ?? recorded?.result;
      if (outcome.kind === "failed" && !out) throw new LocalizedError(analysisFailed);

      setResult(resultOrStandIn(out, outcome.kind === "completed"));
      setJob(recorded);
      setUnscoredPowers(unscored);
      if (flags.extractSkills) refreshProfileSkills(apollo);
      onDone?.();
    } catch (e) {
      setError(localizedMessageOf(e) ?? userErrorMessage(intl, e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ExtractionDialogFrame id="analysis" title={title} onClose={onClose} preventClose={busy && !jobAccepted}
      dataGraphql="StartDocumentsExtraction, DocumentsExtractionEvents, CreateDocument, ExtractionJobResult, PowersData"
      footer={result ? (
          <div className="flex justify-end border-t border-ink/10 px-5 py-3">
            <Button id="analysis-done" variant="primary" size="compact" onClick={onClose}>
              <FormattedMessage id="save.extraction.done" defaultMessage="Done" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 border-t border-ink/10 px-5 py-3">
            <span id="analysis-selection" className="text-smycles-label text-ink-muted">
              {jobAccepted ? (
                <FormattedMessage
                  id="save.extraction.runningInBackground"
                  defaultMessage="Running in the background - you can close this and keep working. You’ll be notified when it’s done."
                />
              ) : total > 0 ? (
                <FormattedMessage
                  id="save.extraction.selectedCount"
                  defaultMessage="{total, plural, one {# document selected} other {# documents selected}}"
                  values={{ total }}
                />
              ) : (
                <FormattedMessage
                  id="save.extraction.nothingSelected"
                  defaultMessage="Nothing selected"
                />
              )}
            </span>
            <Button
              id="analysis-start"
              variant="primary"
              onClick={run}
              // `total > MAX` is unreachable through the UI; it is the backstop for a selection
              // path that ever forgets to check, since genesis refuses such a run anyway.
              disabled={busy || total === 0 || total > MAX_DOCUMENTS_PER_RUN}
              aria-busy={busy}
              size="compact"
              // Fixed min-width so the label swap doesn't change the button
              // size mid-run; the spinner lives INSIDE the pill.
              className="min-w-[10rem] gap-2"
            >
              {busy ? (
                <>
                  <span
                    aria-hidden
                    className="h-3 w-3 shrink-0 animate-spin rounded-full border border-page/40 border-t-page"
                  />
                  <FormattedMessage
                    id="save.extraction.analysing"
                    defaultMessage="Analysing …"
                  />
                </>
              ) : (
                <FormattedMessage
                  id="save.extraction.startAnalysis"
                  defaultMessage="Start analysis"
                />
              )}
            </Button>
          </div>
        )}>
          {result ? (
            <ExtractionResultView result={result} job={job} docs={docs} unscoredPowers={unscoredPowers} />
          ) : (
            <>
              <div>
                <div className="smycles-field-label mb-1.5 text-ink-muted">
                  <FormattedMessage id="save.extraction.newFile" defaultMessage="New file" />
                </div>
                <FileDropZone
                  inputId="analysis-file"
                  variant="zone"
                  clickToOpen
                  accept={ANALYZABLE_ACCEPT}
                  onFiles={(fl) => void addFiles(fl)}
                  label={
                    <FormattedMessage
                      id="save.extraction.dropZone"
                      defaultMessage="Drag a file here or click to upload"
                    />
                  }
                  hint={
                    <FormattedMessage
                      id="save.extraction.dropZoneHint"
                      defaultMessage="PDF, Word, Excel, images · max. 10 MB per file"
                    />
                  }
                />

                {/* bd-requirements#16 and #48. Per pipeline, because the two disagree
                    about what a good document is: only Powers tries to attribute
                    evidence to the person at all, and a bare skill list is
                    worthless there while the Fachkompetenz analysis extracts it, but
                    at the lowest level only (skill-extraction.yaml). The flags are mutually
                    exclusive at every call site (use-service-actions.tsx), so
                    extractPowers alone decides which set is shown.
                    "Attribute", not "skip": HintMiningStage sends EVERY page to
                    the model regardless: the person check is a prompt line
                    (hint-mining-pro.yaml) plus a second per-quote judgement in
                    evidence-filter.yaml. Nothing deterministic gates on the
                    name, so the copy must not promise that it does. */}
                <div className="smycles-hint mt-4">
                  {/* Eyebrow rung + body per the Static Hinweis recipe in
                      UI_CONVENTIONS; the topic goes in the eyebrow slot so the
                      guidance stops competing with "New file" / "From Documents"
                      at the smycles-field-label rung, which read as three peer sections. */}
                  <div className="text-smycles-micro tracking-[0.2em] text-ink-accent">
                    <FormattedMessage
                      id="save.extraction.guideTitle"
                      defaultMessage="Which documents work best"
                    />
                  </div>
                  <ul className="mt-1 smycles-field-meta list-disc space-y-1 pl-4 leading-relaxed text-ink marker:text-ink-muted">
                    {flags.extractPowers ? (
                      <>
                        <li>
                          <FormattedMessage
                            id="save.extraction.guidePowersAboutYou"
                            defaultMessage="It is about you. Evidence the analysis cannot attribute to you is discarded, and whatever is found is written to your profile."
                          />
                        </li>
                        <li>
                          <FormattedMessage
                            id="save.extraction.guidePowersConcrete"
                            defaultMessage="It describes how you work: references, appraisals, feedback, project and role descriptions. A job title or a plain skills list provides no usable evidence."
                          />
                        </li>
                      </>
                    ) : (
                      <>
                        <li>
                          <FormattedMessage
                            id="save.extraction.guideSkillsSuitable"
                            defaultMessage="Anything that says something about your work and project experience or your skills is suitable: CV, certificates, further training, project documentation, job references."
                          />
                        </li>
                        <li>
                          <FormattedMessage
                            id="save.extraction.guideSkillsAboutYou"
                            defaultMessage="Only upload documents that are about you."
                          />
                        </li>
                        <li>
                          <FormattedMessage
                            id="save.extraction.guideSkillsConcrete"
                            defaultMessage="The more concretely an experience is described, the higher the level. A bare list of keywords stays at the lowest level."
                          />
                        </li>
                      </>
                    )}
                  </ul>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="text-smycles-label text-ink-muted">
                    <FormattedMessage
                      id="save.extraction.persistUploads"
                      defaultMessage="Save uploaded files in Documents"
                    />
                  </span>
                  <Switch
                    id="analysis-persist"
                    checked={persistUploads}
                    onChange={setPersistUploads}
                    ariaLabel={intl.formatMessage({
                      id: "save.extraction.persistUploads",
                      defaultMessage: "Save uploaded files in Documents",
                    })}
                  />
                </div>

                {pending.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {pending.map((f, i) => {
                      const duplicate = existingNames.has(f.fileName.toLowerCase());
                      return (
                        <div
                          key={`${f.fileName}-${i}`}
                          className="box-flat bg-page px-3 py-1.5 text-smycles-meta"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <DocumentView variant="inline" file={{ name: f.fileName, mimeType: f.mimeType, content: f.content }} showName />
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => {
                                spentRef.current -= 1;
                                setPending((p) => p.filter((_, j) => j !== i));
                              }}
                              aria-label={intl.formatMessage({
                                id: "common.remove",
                                defaultMessage: "Remove",
                              })}
                              className="shrink-0"
                            >
                              ×{/* i18n-allow: close glyph, not copy */}
                            </Button>
                          </div>
                          {duplicate ? (
                            <div className="mt-0.5 text-smycles-micro text-ink-attention">
                              <FormattedMessage
                                id="save.extraction.duplicateWarning"
                                defaultMessage="Already exists in Documents - the stored document is analysed, no re-upload."
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <ExtractionDocumentPicker docs={docs} groups={groups} docsLoading={docsLoading} docsLoadError={docsLoadError}
                selectedIds={selectedIds} room={room} onToggle={toggleDoc} onToggleMany={toggleMany} />

              {error ? <div id="analysis-error" className="text-smycles-label text-danger">{error}</div> : null}
            </>
          )}
    </ExtractionDialogFrame>
  );
}
